import { useEffect } from "react";
import {
	Outlet,
	Route,
	Routes,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { HasApplicationForm } from "./components/HasApplicationForm";
import { RequireAuthentication } from "./components/RequireAuthentication";
import { CompleteProfileProvider } from "./contexts/complete-profile.context";
import { IdentityVerification } from "./pages/identity-verification";
import { Capture } from "./pages/identity-verification/capture";
import { IDUpload } from "./pages/identity-verification/id-upload";
import HowItWorks from "./pages/how-it-works";
import Login from "./pages/login";
import BackgroundCheck from "./pages/background-check";
import BankAccountInformation from "./pages/bank-account-information/";
import NotFound from "./pages/not-found/NotFound";
import CertificatePage from "./pages/certificates";
import TelegramSoftskillsPage from "./pages/telegram-token";
import SwitchTrackPage from "./pages/switch-track";
import { useAuth0 } from "@auth0/auth0-react";
import { Loading } from "./components/Loading";
import { PostHogPageview } from "./components/PostHogPageview";
import { RegionGuard } from "./components/region-guard";
import { trackRegions } from "./constants";
import { BasicApplicationGuard } from "./guards/basic-application.guard";
import { CommunityMembershipGuard } from "./guards/community-membership.guard";
import { IdentityVerificationGuard } from "./guards/identity-verification.guard";
import { LayoutV2 } from "./components/LayoutV2";
import { ApplyV2 } from "./pages/apply/apply-v2";
import { ProWebsiteLayout } from "./components/ProWebsiteLayout";
import ProProfilePage from "./pages/my-laborpass";
import { AddJob } from "./pages/my-laborpass/add-job";
import { AddCertification } from "./pages/my-laborpass/add-certification";
import { NewDashboard } from "./pages/dashboard";
import { AddEducation } from "./pages/my-laborpass/add-education";
import { HasCompletedPortfolioRequirements } from "./guards/has-completed-portfolio-requirements.guard";
import ChatWithAInaPage from "./pages/my-laborpass/chat-with-AIna";
import SetupLaborPass from "./pages/setup-laborpass";
import MyJobApplications from "./pages/my-job-applications";
import NotificationPreferencesPage from "./pages/my-job-applications/notification-preferences";
import Communities from "./pages/communities";
import AnnouncementDetailsPage from "./pages/communities/announcements/[id]";
import CommunityInvitePage from "./pages/community-invite";
import CommunityInviteCallbackPage from "./pages/community-invite/callback";
import Opportunities from "./pages/opportunities";
import CommunityOpportunities from "./pages/opportunities/communities";
import CommunityJobOpportunityDetails from "./pages/opportunities/communities/[id]/index";
import CommunityJobOpportunityApplication from "./pages/opportunities/communities/[id]/apply/index";
import LaborHackJobs from "./pages/opportunities/laborhack";
import LaborHackJobDetails from "./pages/opportunities/laborhack/[id]/index";
import LaborHackJobApplication from "./pages/opportunities/laborhack/[id]/apply/index";
import TradesPage from "./pages/trades";
import SettingsPage from "./pages/settings";
import { TOGGLE_KEYS, useToggles } from "./hooks/useToggles";
import DocumentViewer from "./pages/legal";
import GuestApplicantSignupPage from "./pages/guest-applicant-signup";

type CustomNavigateProps = {
	to: string;
};

export const CustomNavigate = ({ to }: CustomNavigateProps) => {
	const navigate = useNavigate();

	useEffect(() => {
		navigate(to);
	});

	return <></>;
};

const storedQueryParamKeys = [
	"leadSource",
	"referredBy",
	"preferredTrack",
	"src_email",
];

function App() {
	const { isLoading } = useAuth0();
	const { toggles, isLoading: isLoadingToggles } = useToggles();
	const communityInviteUiEnabled = !!toggles[TOGGLE_KEYS.COMMUNITY_INVITE_UI];
	const leanServicesModeEnabled = toggles[TOGGLE_KEYS.LEAN_SERVICES_MODE] === true;

	const [searchParams, setSearchParams] = useSearchParams();

	const isBaseUrl = window.location.pathname === "/";

	useEffect(() => {
		if (!isBaseUrl) {
			return;
		}

		const hasStoredQueryParams = storedQueryParamKeys.some((key) =>
			searchParams.get(key)
		);

		if (!hasStoredQueryParams) {
			return;
		}

		storedQueryParamKeys.forEach((key) => {
			if (searchParams.get(key)) {
				localStorage.setItem(key, searchParams.get(key) || "");

				searchParams.delete(key);
			}
		});

		setSearchParams(searchParams);
	}, [searchParams, setSearchParams, isBaseUrl]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<CompleteProfileProvider>
			<PostHogPageview />
			<Routes>
				<Route
					path="/community-invite"
					element={
						isLoadingToggles ? (
							<Loading />
						) : communityInviteUiEnabled ? (
							<CommunityInvitePage />
						) : (
							<NotFound />
						)
					}
				/>
				<Route
					path="/community-invite/callback"
					element={
						isLoadingToggles ? (
							<Loading />
						) : communityInviteUiEnabled ? (
							<CommunityInviteCallbackPage />
						) : (
							<NotFound />
						)
					}
				/>
				<Route
					path="/"
					element={
						<RequireAuthentication>
							<Outlet />
						</RequireAuthentication>
					}
				>
					<Route
						path="/my-job-applications"
						element={
							<HasApplicationForm>
								<LayoutV2 />
							</HasApplicationForm>
						}
					>
						<Route index element={<MyJobApplications />} />
						<Route
							path="notification-preferences"
							element={<NotificationPreferencesPage />}
						></Route>
					</Route>
					<Route
						path="/communities"
						element={
							<HasApplicationForm>
								<CommunityMembershipGuard redirectTo="/">
									<LayoutV2 />
								</CommunityMembershipGuard>
							</HasApplicationForm>
						}
					>
						<Route index element={<Communities />} />
						<Route
							path="announcements/:id"
							element={<AnnouncementDetailsPage />}
						/>
					</Route>
					<Route
						path="/opportunities"
						element={
							<HasApplicationForm>
								<LayoutV2 />
							</HasApplicationForm>
						}
					>
						<Route index element={<Opportunities />} />
						<Route
							path="communities"
							element={
								<CommunityMembershipGuard redirectTo="/opportunities">
									<Outlet />
								</CommunityMembershipGuard>
							}
						>
							<Route index element={<CommunityOpportunities />} />
							<Route path=":id">
								<Route index element={<CommunityJobOpportunityDetails />} />
								<Route
									path="apply"
									element={<CommunityJobOpportunityApplication />}
								/>
							</Route>
						</Route>
						<Route path="laborhack">
							<Route index element={<LaborHackJobs />} />
							<Route path=":id">
								<Route index element={<LaborHackJobDetails />} />
								<Route path="apply" element={<LaborHackJobApplication />} />
							</Route>
						</Route>
					</Route>
					<Route
						path="/my-laborpass"
						element={
							<HasApplicationForm>
								<ProWebsiteLayout />
							</HasApplicationForm>
						}
					>
						<Route path="add-job" element={<AddJob />}></Route>
						<Route
							path="add-certification"
							element={<AddCertification />}
						></Route>
						<Route path="add-education" element={<AddEducation />}></Route>
						<Route path="chat-with-aina" element={<ChatWithAInaPage />}></Route>
						<Route index element={<ProProfilePage />}></Route>
					</Route>
					<Route path="/" element={<LayoutV2 />}>
						<Route path="/apply" element={<ApplyV2 />} />
						<Route
							path="/setup-laborpass"
							element={
								<HasApplicationForm>
									<SetupLaborPass />
								</HasApplicationForm>
							}
						/>
						<Route
							element={
								<HasApplicationForm>
									<HasCompletedPortfolioRequirements>
										<Outlet />
									</HasCompletedPortfolioRequirements>
								</HasApplicationForm>
							}
						>
							<Route index element={<NewDashboard />} />
							<Route path="/dashboard" element={<CustomNavigate to="/" />} />
							<Route path="/settings" element={<SettingsPage />}></Route>
							<Route
								path="/identity-verification"
								element={
									isLoadingToggles ? (
										<Loading />
									) : leanServicesModeEnabled ? (
										<CustomNavigate to="/" />
									) : (
										<IdentityVerification />
									)
								}
							/>
							<Route
								path="/background-check"
								element={
									<RegionGuard regions={trackRegions.LOCALGIG}>
										<BackgroundCheck />
									</RegionGuard>
								}
							/>
							<Route
								path="/bank-account-information"
								element={
									<RegionGuard regions={trackRegions.LOCALGIG}>
										<BankAccountInformation />
									</RegionGuard>
								}
							/>
							<Route
								path="/complete-profile/id-upload"
								element={
									isLoadingToggles ? (
										<Loading />
									) : leanServicesModeEnabled ? (
										<CustomNavigate to="/" />
									) : (
										<IDUpload />
									)
								}
							/>
							<Route
								path="/complete-profile/capture"
								element={
									isLoadingToggles ? (
										<Loading />
									) : leanServicesModeEnabled ? (
										<CustomNavigate to="/" />
									) : (
										<Capture />
									)
								}
							/>
							<Route path="/how-it-works" element={<HowItWorks />} />

							<Route path="/trades">
								<Route path="*" element={<TradesPage />} />
							</Route>
							<Route
								path="/certificates"
								element={
									<RegionGuard regions={trackRegions.LOCALGIG}>
										<Outlet />
									</RegionGuard>
								}
							>
								<Route index element={<CertificatePage />} />
							</Route>
							<Route path="/switch-track" element={<SwitchTrackPage />} />
						</Route>
						<Route path="*" element={<div>Bad route</div>} />
					</Route>
				</Route>

				<Route path="/login" element={<Login />} />
				<Route
					path="/guest-applicant/signup"
					element={<GuestApplicantSignupPage />}
				/>
				<Route path="/legal" element={<DocumentViewer />} />

				<Route path="/telegram-token">
					<Route
						index
						element={
							<RequireAuthentication>
								<BasicApplicationGuard>
									<IdentityVerificationGuard>
										<TelegramSoftskillsPage />
									</IdentityVerificationGuard>
								</BasicApplicationGuard>
							</RequireAuthentication>
						}
					/>
				</Route>

				<Route path="*" element={<NotFound />} />
			</Routes>
		</CompleteProfileProvider>
	);
}

export default App;
